import React, { useState } from 'react';
import {
  AnnouncementDataTable,
  AnnouncementsErrorBoundary,
  NoAnnouncementData,
} from '../components';
import { Button, Dropdown, LoadingState, PermissionGuard } from '@/shared';
import { Link } from '@tanstack/react-router';
import { AddHomeAnnouncementModal } from '../components/modals';
import { useAnnouncementOverview } from '../hooks/useAnnouncementQueries';
import type { Announcement } from '../api';

// ---------- Types ----------
type AnnouncementTimeRange = 30 | 60 | 90;

// ---------- Component ----------
const AnnouncementsPageContent: React.FC = () => {
  // const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedRange, setSelectedRange] = useState<AnnouncementTimeRange>(30);

  const [showAddAnnouncement, setShowAddAnnouncement] =
    useState<boolean>(false);

  const { data, isLoading } = useAnnouncementOverview(30);
  console.log('data--> ', data);

  if (isLoading || !data) {
    return (
      <LoadingState message="Loading announcement overview..." fullScreen />
    );
  }

  const timeOptions = [
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 60 days', value: 60 },
    { label: 'Last 90 days', value: 90 },
  ];

  return (
    <div className="min-h-screen bg-background w-full">
      {!data ? (
        <NoAnnouncementData />
      ) : (
        <div>
          {/* Header */}
          <div className="mb-8 px-0 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-2 py-2">
              <p className="text-lg md:text-xl text-text-secondary font-bold">
                Total house announcements
              </p>

              <div className="relative">
                <Dropdown
                  options={timeOptions}
                  value={selectedRange}
                  onChange={(val) =>
                    setSelectedRange(val as AnnouncementTimeRange)
                  }
                  className="text-sm"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="flex items-center gap-4">
              <span className="text-5xl text-text-primary font-bold">
                {data?.totalAnnouncements}
              </span>
            </div>

            {/* Progress Bars */}
            <div className="grid grid-cols-12 gap-2 my-4 md:my-9">
              <div className="h-[6px] bg-[#B6B0FB] col-span-4" />
              <div className="h-[6px] bg-[#BBEEED] col-span-1" />
              <div className="h-[6px] bg-[#FBDE9D] col-span-3" />
              <div className="h-[6px] bg-[#95A1FC] col-span-4" />
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  title: 'Active house announcements',
                  value: data?.activeCount || 0,
                },
                {
                  title: 'Scheduled house announcements',
                  value: data?.scheduledCount,
                },
                {
                  title: 'Expired house announcements',
                  value: data?.expiredCount,
                },
                {
                  title: 'Active audience',
                  value: data?.activeAudience,
                },
              ].map((card, idx) => (
                <div key={idx} className="p-3 bg-white rounded-md shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-[#B6B0FB] h-2 w-2 rounded-full" />
                    <span className="text-text-secondary text-sm md:text-base">{card.title}</span>
                  </div>
                  <span className="text-xl text-text-primary font-bold">
                    {card.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <PermissionGuard permission="postAnnouncements">
            <div className=" mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
              <div className="w-full flex flex-col md:flex-row items-center justify-center gap-4 md:justify-between p-4 card">
                <div className="flex items-center gap-6">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M29.9948 21.667V18.3337H36.6615V21.667H29.9948ZM31.9948 33.3337L26.6615 29.3337L28.6615 26.667L33.9948 30.667L31.9948 33.3337ZM28.6615 13.3337L26.6615 10.667L31.9948 6.66699L33.9948 9.33366L28.6615 13.3337ZM8.32812 31.667V25.0003H6.66146C5.74479 25.0003 4.96007 24.6739 4.30729 24.0212C3.65451 23.3684 3.32812 22.5837 3.32812 21.667V18.3337C3.32812 17.417 3.65451 16.6323 4.30729 15.9795C4.96007 15.3267 5.74479 15.0003 6.66146 15.0003H13.3281L21.6615 10.0003V30.0003L13.3281 25.0003H11.6615V31.667H8.32812ZM23.3281 25.5837V14.417C24.0781 15.0837 24.6823 15.8962 25.1406 16.8545C25.599 17.8128 25.8281 18.8614 25.8281 20.0003C25.8281 21.1392 25.599 22.1878 25.1406 23.1462C24.6823 24.1045 24.0781 24.917 23.3281 25.5837Z"
                      fill="#5761EB"
                    />
                  </svg>

                  <div>
                    <p className="text-lg md:text-2xl text-text-primary font-bold">
                      Add new house announcements
                    </p>
                    <p className="text-base md:text-xl text-text-secondary">
                      Manage your properties with ease
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <Button
                    className="text-base md:!text-lg !font-bold px-6 py-3"
                    onClick={() => setShowAddAnnouncement(true)}
                  >
                    Add house announcements
                  </Button>
                </div>
              </div>
            </div>
          </PermissionGuard>

          <div className="mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <h3 className="text-text-primary text-xl font-bold">
                House announcements
              </h3>
              <Link
                to="/announcements/all"
                className="text-[#357AE1] cursor-pointer hover:underline"
              >
                See more
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto">
            <AnnouncementDataTable
              data={data?.latestAnnouncements as Announcement[]} className='min-h-[50vh]'
            />
          </div>

          {showAddAnnouncement && (
            <AddHomeAnnouncementModal
                isOpen={showAddAnnouncement}
                onClose={() => setShowAddAnnouncement(!showAddAnnouncement)}
              />
            )}
        </div>
      )}
    </div>
  );
};

export const AnnouncementsPage: React.FC = () => (
  <AnnouncementsErrorBoundary onRetry={() => window.location.reload()}>
    <AnnouncementsPageContent />
  </AnnouncementsErrorBoundary>
);
